import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ui/accordion.tsx';
import { AccordionTriggerLabel } from '@admin/components/workspace/accordion-trigger-label.tsx';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Button } from '@ui/button.tsx';
import { useNavigate } from '@tanstack/react-router';
import { ColorPicker } from '@r/components/ui/color-picker.tsx';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/card.tsx';
import { useWorkspaceFormStore } from '@admin/context/workspace-form-context.tsx';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useCreateWorkspace, apiAdminconfigsPost } from '@r/api/generated/client.ts';
import { useAllConfigs, findConfig, getConfigsFromResponse } from '@r/hooks/use-all-configs.ts';
import type {
  ConfigConfigReadModel,
  ConfigConfigWriteModel
} from '@r/api/generated/client.schemas.ts';
import { MenuItem } from '@ui/menu-item.tsx';
import type {
  WorkspaceWorkspaceWriteModel,
  WorkspaceConfigWorkspaceWriteModel
} from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

const formSchema = z.object({
  background: z.string(),
  foreground: z.string(),
  primary: z.string(),
  'primary-foreground': z.string(),
  card: z.string(),
  'card-foreground': z.string(),
  footer: z.string()
});

type FormSchema = z.infer<typeof formSchema>;

type WorkspaceFormDataLocal = WorkspaceWorkspaceWriteModel;

const cleanHSLString = (hslString: string): string => {
  const trimmedString = hslString.trim();
  const hslRegex = /^(hsla?)\(([^)]+)\)$/i;
  const match = hslRegex.exec(trimmedString);

  if (match?.[2]) {
    const content = match[2];
    return content.replace(/,\s*/g, ' ').replace(/\s+/g, ' ').trim();
  }

  return trimmedString.replace(/,\s*/g, ' ').replace(/;\s*$/, '').trim();
};

type WorkspaceConfigItem = WorkspaceConfigWorkspaceWriteModel;

export const CreateWorkspaceStep2 = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const { updateFormData, workspaces } = useWorkspaceFormStore();
  const user = useUser();
  const activeWorkspace = user.data && getActiveWorkspaceUniversal(user.data.visibleWorkspaces);
  const activeWorkspaceId: number | undefined = activeWorkspace?.workspace?.id;
  const formData: Partial<WorkspaceFormDataLocal> = activeWorkspaceId
    ? workspaces[activeWorkspaceId]
    : { workspaceConfigs: [], name: '' };

  // Fetch de toutes les configs disponibles (mutualisé avec les autres steps)
  const configsQuery = useAllConfigs();
  const configsFromApi = getConfigsFromResponse(configsQuery.data);

  const configs = formData?.workspaceConfigs || [];

  // Fonction helper pour trouver une config style par sa key et retourner son IRI
  const getStyleConfigIri = (key: string): string | null => {
    const styleConfig = findConfig(configsFromApi, 'style', key);
    return styleConfig?.id ? `/configs/${styleConfig.id}` : null;
  };

  // Fonction helper pour récupérer la valeur d'une config style depuis formData
  const getStyleConfigValue = (
    configs: WorkspaceConfigItem[],
    key: string,
    defaultValue: string
  ): string => {
    const configIri = getStyleConfigIri(key);
    if (!configIri) return defaultValue;

    const foundConfig = configs.find((config) => config.config === configIri);
    return foundConfig?.value || defaultValue;
  };

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      background: getStyleConfigValue(
        configs as WorkspaceConfigItem[],
        'background',
        'hsl(0 0% 100%)'
      ),
      foreground: getStyleConfigValue(
        configs as WorkspaceConfigItem[],
        'foreground',
        'hsl(0 0% 0%)'
      ),
      primary: getStyleConfigValue(configs as WorkspaceConfigItem[], 'primary', 'hsl(0 0% 100%)'),
      'primary-foreground': getStyleConfigValue(
        configs as WorkspaceConfigItem[],
        'primary-foreground',
        'hsl(0 0% 0%)'
      ),
      card: getStyleConfigValue(configs as WorkspaceConfigItem[], 'card', 'hsl(0 0% 100%)'),
      'card-foreground': getStyleConfigValue(
        configs as WorkspaceConfigItem[],
        'card-foreground',
        'hsl(0 0% 0%)'
      ),
      footer: getStyleConfigValue(configs as WorkspaceConfigItem[], 'footer', 'hsl(0 0% 0%)')
    },
    mode: 'onChange'
  });

  const foreground = form.watch('foreground');
  const primary = form.watch('primary');
  const primaryForeground = form.watch('primary-foreground');
  const background = form.watch('background');
  const footerBackground = form.watch('footer');
  const card = form.watch('card');
  const cardForeground = form.watch('card-foreground');

  const navigate = useNavigate();

  const submitMutation = useCreateWorkspace({
    mutation: {
      onSuccess: async () => {
        await navigate({ to: '/create-workspace/step/3' });
      },
      onError: (error: unknown) => {
        if (isOrvalErrorFromPath(error, 'put', '/admin/workspaces')) {
          showOrvalErrorToast(error);
          return;
        }

        toast.error(t('form.workspace.unknown.error'));
      }
    }
  });

  async function performSubmit(values: FormSchema) {
    const configsFromApi = getConfigsFromResponse(configsQuery.data);
    if (!configsFromApi?.length) {
      toast.error(t('form.workspace.configs.not.loaded'));
      return;
    }

    // Trouver toutes les configs style nécessaires
    const styleKeys = [
      'background',
      'foreground',
      'primary',
      'primary-foreground',
      'card',
      'card-foreground',
      'footer'
    ];

    const styleConfigsMap = new Map<string, ConfigConfigReadModel>();
    for (const key of styleKeys) {
      const config = findConfig(configsFromApi, 'style', key);
      if (config?.id) {
        styleConfigsMap.set(key, config);
      }
    }

    // Créer les configs manquantes avant d'envoyer
    const missingConfigs = styleKeys.filter((key) => !styleConfigsMap.has(key));
    if (missingConfigs.length > 0) {
      // Mapping des keys vers leurs titres et descriptions
      const configMetadata: Record<string, { title: string; description: string }> = {
        foreground: { title: 'Foreground', description: 'Couleur du texte principal' },
        primary: { title: 'Primary', description: 'Couleur primaire' },
        'primary-foreground': {
          title: 'Primary Foreground',
          description: 'Couleur du texte sur fond primaire'
        },
        'card-foreground': {
          title: 'Card Foreground',
          description: 'Couleur du texte sur les cartes'
        },
        footer: { title: 'Footer', description: 'Couleur du footer' }
      };

      // Créer toutes les configs manquantes (ou récupérer celles qui existent déjà)
      const createPromises = missingConfigs.map(async (key) => {
        const metadata = configMetadata[key] || { title: key, description: '' };
        const newConfig: ConfigConfigWriteModel = {
          type: 'style',
          key,
          isPublic: true,
          isSecret: false,
          title: metadata.title,
          description: metadata.description
        };

        try {
          await apiAdminconfigsPost(newConfig);
        } catch (error: unknown) {
          // Si la config existe déjà (erreur 500 avec duplicate key), on continue
          // On récupérera la config existante lors du refresh
          const errorObj = error as {
            response?: {
              status?: number;
              data?: {
                message?: string;
                code?: number;
              };
            };
          };

          const errorMessage = errorObj?.response?.data?.message || String(error) || '';

          const isDuplicateKeyError =
            errorMessage.includes('duplicate key') ||
            errorMessage.includes('Unique violation') ||
            errorMessage.includes('already exists') ||
            (errorObj?.response?.status === 500 &&
              errorMessage.includes('violates unique constraint'));

          if (isDuplicateKeyError) {
            return; // On continue, on récupérera la config lors du refresh
          }
          // Pour les autres erreurs, on propage
          throw error;
        }
      });

      try {
        await Promise.all(createPromises);

        // Re-fetch les configs pour obtenir les IDs (nouvelles configs + configs existantes)
        const refreshedConfigs = await configsQuery.refetch();
        const refreshedConfigsList = getConfigsFromResponse(refreshedConfigs.data);
        if (refreshedConfigsList?.length) {
          // Mettre à jour le styleConfigsMap avec toutes les configs trouvées
          for (const key of missingConfigs) {
            const foundConfig = findConfig(refreshedConfigsList, 'style', key);
            if (foundConfig?.id) {
              styleConfigsMap.set(key, foundConfig);
            }
          }
        }
      } catch (error) {
        showOrvalErrorToast(error, t('form.workspace.configs.create.error'));
        return;
      }
    }

    // Construire les workspaceConfigs avec le nouveau format - seulement pour les configs qui existent
    const transformedWorkspaceConfigs: WorkspaceConfigItem[] = [];

    // Ajouter chaque config seulement si elle existe dans la base
    const backgroundConfig = styleConfigsMap.get('background');
    if (backgroundConfig?.id) {
      transformedWorkspaceConfigs.push({
        value: cleanHSLString(values.background),
        config: `/configs/${backgroundConfig.id}`
      });
    }

    const foregroundConfig = styleConfigsMap.get('foreground');
    if (foregroundConfig?.id) {
      transformedWorkspaceConfigs.push({
        value: cleanHSLString(values.foreground),
        config: `/configs/${foregroundConfig.id}`
      });
    }

    const primaryConfig = styleConfigsMap.get('primary');
    if (primaryConfig?.id) {
      transformedWorkspaceConfigs.push({
        value: cleanHSLString(values.primary),
        config: `/configs/${primaryConfig.id}`
      });
    }

    const primaryForegroundConfig = styleConfigsMap.get('primary-foreground');
    if (primaryForegroundConfig?.id) {
      transformedWorkspaceConfigs.push({
        value: cleanHSLString(values['primary-foreground']),
        config: `/configs/${primaryForegroundConfig.id}`
      });
    }

    const cardConfig = styleConfigsMap.get('card');
    if (cardConfig?.id) {
      transformedWorkspaceConfigs.push({
        value: cleanHSLString(values.card),
        config: `/configs/${cardConfig.id}`
      });
    }

    const cardForegroundConfig = styleConfigsMap.get('card-foreground');
    if (cardForegroundConfig?.id) {
      transformedWorkspaceConfigs.push({
        value: cleanHSLString(values['card-foreground']),
        config: `/configs/${cardForegroundConfig.id}`
      });
    }

    const footerConfig = styleConfigsMap.get('footer');
    if (footerConfig?.id) {
      transformedWorkspaceConfigs.push({
        value: cleanHSLString(values.footer),
        config: `/configs/${footerConfig.id}`
      });
    }

    if (!activeWorkspaceId) {
      throw new Error('No active workspace');
    }
    const newFormData = await updateFormData(activeWorkspaceId, {
      workspaceConfigs: transformedWorkspaceConfigs
    });
    submitMutation.mutate({
      id: String(activeWorkspaceId),
      data: newFormData as unknown as import('@r/api/generated/client.schemas.ts').WorkspaceWorkspaceWriteModel
    });
  }

  async function onSubmit(values: FormSchema) {
    await performSubmit(values);
  }

  if (!activeWorkspaceId) {
    console.error("Aucun espace de travail actif ou ID d'espace de travail introuvable.");
    return <div>{t('common.error.no_active_workspace', 'No active workspace found.')}</div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-0.5">
        <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
          <AccordionItem value="item-1" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['background', 'foreground']}
                label={t('form.workspace.main.colors')}
                form={form}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <span className="mb-2 mt-1 font-light peer-disabled:cursor-not-allowed peer-disabled:opacity-30 text-foreground text-[0.875rem] pl-[0.125rem] whitespace-pre-wrap">
                <Trans
                  i18nKey={t('form.workspace.main.colors.description')}
                  components={{ 1: <br /> }}
                />
              </span>
              <div className="flex flex-row space-x-4 w-full">
                <FormField
                  control={form.control}
                  name="background"
                  render={({ field }) => (
                    <FormItem className="flex-1 mb-4">
                      <FormLabel>{t('form.workspace.background')}</FormLabel>
                      <div className="flex items-center p-2 space-x-2 bg-gray-100 rounded-md">
                        <ColorPicker
                          className="h-[1.5rem] w-[1.5rem] border-gray-400"
                          onChange={(v) => {
                            field.onChange(v);
                          }}
                          value={field.value}
                        />
                        <span className="text-sm text-muted-foreground">{field.value}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="foreground"
                  render={({ field }) => (
                    <FormItem className="flex-1 mb-4">
                      <FormLabel>{t('form.workspace.foreground')}</FormLabel>
                      <div className="flex items-center p-2 space-x-2 bg-gray-100 rounded-md">
                        <ColorPicker
                          className="h-[1.5rem] w-[1.5rem] border-gray-400"
                          onChange={(v) => {
                            field.onChange(v);
                          }}
                          value={field.value}
                        />
                        <span className="text-sm text-muted-foreground">{field.value}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div
                className="h-[15rem] w-full rounded-md p-2 flex items-center justify-center border-gray-200 border"
                style={{ backgroundColor: background }}
              >
                <div className="flex flex-col items-center space-y-2 w1/2">
                  <div className="w-1/2 text-2xl font-bold">
                    <span style={{ color: foreground }}>Lorem ipsum</span>
                  </div>
                  <div className="w-1/2 text-xs" style={{ color: foreground }}>
                    Lorem ipsum dolor sit amet consectetur. Convallis eu commodo venenatis vulputate
                    quis dictumst eget. Quis lobortis aliquet odio proin volutpat.
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-2" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['card', 'card-foreground']}
                label={t('form.workspace.card.colors')}
                form={form}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <span className="mb-2 mt-1 font-light peer-disabled:cursor-not-allowed peer-disabled:opacity-30 text-foreground text-[0.875rem] pl-[0.125rem]">
                {t('form.workspace.card.colors.description')}
              </span>
              <div className="flex flex-row space-x-4 w-full">
                <FormField
                  control={form.control}
                  name="card"
                  render={({ field }) => (
                    <FormItem className="flex-1 mb-4">
                      <FormLabel>{t('form.workspace.background')}</FormLabel>
                      <div className="flex items-center p-2 space-x-2 bg-gray-100 rounded-md">
                        <ColorPicker
                          className="h-[1.5rem] w-[1.5rem] border-gray-400"
                          onChange={(v) => {
                            field.onChange(v);
                          }}
                          value={field.value}
                        />
                        <span className="text-sm text-muted-foreground">{field.value}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="card-foreground"
                  render={({ field }) => (
                    <FormItem className="flex-1 mb-4">
                      <FormLabel>{t('form.workspace.foreground')}</FormLabel>
                      <div className="flex items-center p-2 space-x-2 bg-gray-100 rounded-md">
                        <ColorPicker
                          className="h-[1.5rem] w-[1.5rem] border-gray-400"
                          onChange={(v) => {
                            field.onChange(v);
                          }}
                          value={field.value}
                        />
                        <span className="text-sm text-muted-foreground">{field.value}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div
                className="h-[15rem] w-full rounded-md p-2 flex items-center justify-center border-gray-200 border"
                style={{ backgroundColor: background }}
              >
                <div className="flex flex-col items-center">
                  <Card
                    className="flex flex-col w-2/3"
                    style={{
                      backgroundColor: card
                    }}
                  >
                    <CardHeader className="space-y-0">
                      <CardTitle
                        style={{ color: cardForeground }}
                        className="flex items-center space-x-2 font-author-complete font-regular text-xl"
                      >
                        Title example
                      </CardTitle>
                    </CardHeader>

                    <CardContent style={{ color: cardForeground }}>
                      Lorem ipsum dolor sit amet consectetur. Convallis eu commodo venenatis
                      vulputate quis dictumst eget. Je suis un lien. Quis lobortis aliquet odio
                      proin volutpat.
                    </CardContent>
                  </Card>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>{' '}
          <AccordionItem value="item-3" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['primary', 'primary-foreground']}
                label={t('form.workspace.button.colors')}
                form={form}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <span className="mb-2 mt-1 font-light peer-disabled:cursor-not-allowed peer-disabled:opacity-30 text-foreground text-[0.875rem] pl-[0.125rem]">
                {t('form.workspace.button.colors.description')}
              </span>
              <div className="flex flex-row space-x-4 w-full">
                <FormField
                  control={form.control}
                  name="primary"
                  render={({ field }) => (
                    <FormItem className="flex-1 mb-4">
                      <FormLabel>{t('form.workspace.background')}</FormLabel>
                      <div className="flex items-center p-2 space-x-2 bg-gray-100 rounded-md">
                        <ColorPicker
                          className="h-[1.5rem] w-[1.5rem] border-gray-400"
                          onChange={(v) => {
                            field.onChange(v);
                          }}
                          value={field.value}
                        />
                        <span className="text-sm text-muted-foreground">{field.value}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="primary-foreground"
                  render={({ field }) => (
                    <FormItem className="flex-1 mb-4">
                      <FormLabel>{t('form.workspace.foreground')}</FormLabel>
                      <div className="flex items-center p-2 space-x-2 bg-gray-100 rounded-md">
                        <ColorPicker
                          className="h-[1.5rem] w-[1.5rem] border-gray-400"
                          onChange={(v) => {
                            field.onChange(v);
                          }}
                          value={field.value}
                        />
                        <span className="text-sm text-muted-foreground">{field.value}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div
                className="h-[15rem] w-full rounded-md p-2 flex items-center justify-center border-gray-200 border"
                style={{ backgroundColor: background }}
              >
                <div className="flex flex-col items-center">
                  <Button type="button" backgroundColor={primary} color={primaryForeground}>
                    Button
                  </Button>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>{' '}
          <AccordionItem value="item-4" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['footer']}
                label={t('form.workspace.footer.colors')}
                form={form}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <span className="mb-2 mt-1 font-light peer-disabled:cursor-not-allowed peer-disabled:opacity-30 text-foreground text-[0.875rem] pl-[0.125rem]">
                {t('form.workspace.footer.colors.description')}
              </span>
              <div className="flex flex-row space-x-4 w-full">
                <FormField
                  control={form.control}
                  name="footer"
                  render={({ field }) => (
                    <FormItem className="flex-1 mb-4">
                      <FormLabel>{t('form.workspace.footer.colors')}</FormLabel>
                      <div className="flex items-center p-2 space-x-2 w-1/2 bg-gray-100 rounded-md">
                        <ColorPicker
                          className="h-[1.5rem] w-[1.5rem] border-gray-400"
                          onChange={(v) => {
                            field.onChange(v);
                          }}
                          value={field.value}
                        />
                        <span className="text-sm text-muted-foreground">{field.value}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <Card
                className="w-full text-base rounded-none border-0 font-inter text-primary-foreground sm:rounded-3xl"
                style={{ backgroundColor: footerBackground }}
              >
                <CardContent className="p-5">
                  <div className="p-2 mb-2 text-4xl font-medium font-author-complete">
                    {formData.name}
                  </div>

                  <div className="flex flex-row mb-8 space-x-3 w-full">
                    <MenuItem className="hover:bg-background hover:text-foreground w-fit">
                      {t('footer.home')}
                    </MenuItem>

                    <MenuItem className="hover:bg-background hover:text-foreground w-fit">
                      {t('footer.program')}
                    </MenuItem>
                    <MenuItem className="hover:bg-background hover:text-foreground w-fit">
                      {t('footer.performances')}
                    </MenuItem>

                    <MenuItem className="hover:bg-background hover:text-foreground w-fit">
                      {t('footer.account')}
                    </MenuItem>
                  </div>

                  <div className="flex space-x-3 w-full">
                    <MenuItem className="hover:bg-background hover:text-foreground w-fit">
                      {t('footer.legal.notice')}
                    </MenuItem>

                    <MenuItem className="hover:bg-background hover:text-foreground w-fit">
                      {t('footer.privacy.policy')}
                    </MenuItem>

                    <MenuItem className="hover:bg-background hover:text-foreground w-fit">
                      {t('footer.terms.and.conditions')}
                    </MenuItem>
                  </div>
                </CardContent>
              </Card>
            </AccordionContent>
          </AccordionItem>{' '}
        </Accordion>
        <Button
          rounded="full"
          type="submit"
          loading={form.formState.isSubmitting || submitMutation.isPending}
          disabled={!configsFromApi?.length || configsQuery.isPending}
        >
          {t('form.workspace.next.step')}
        </Button>
      </form>
    </Form>
  );
};
